/**
 * System Details Component
 * Visual graphs and metrics for system resources, database connections,
 * and LLM provider status
 * Requirements: 11.3
 */

import React, { useEffect, useState, useRef } from 'react';

interface SystemHealth {
  cpu: {
    user: number;
    system: number;
  };
  memory: {
    heapUsed: number;
    heapTotal: number;
    rss: number;
    external: number;
  };
  uptime: number;
  connections: {
    mongodb: {
      status: 'healthy' | 'unhealthy';
      poolSize?: number;
      availableConnections?: number;
      responseTimeMs?: number;
    };
    redis: {
      status: 'healthy' | 'unhealthy';
      responseTimeMs?: number;
    };
    websocket: {
      activeConnections: number;
    };
  };
  providers: Record<string, boolean>;
  activeUsers: number;
  timestamp: string;
}

interface HistoryEntry {
  timestamp: number;
  memoryPercent: number;
  rss: number;
  cpuUser: number;
  cpuSystem: number;
  mongoMs?: number;
  redisMs?: number;
}

const MAX_HISTORY = 30;

/** SVG sparkline from an array of 0–100 values */
const Sparkline: React.FC<{
  data: number[];
  color: string;
  height?: number;
  width?: number;
  fillOpacity?: number;
}> = ({ data, color, height = 60, width = 280, fillOpacity = 0.15 }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const linePath = `M${points.join(' L')}`;
  const fillPath = `${linePath} L${width},${height} L0,${height} Z`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={fillOpacity * 2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#grad-${color.replace('#', '')})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Current value dot */}
      {data.length > 0 && (
        <circle
          cx={width}
          cy={height - (data[data.length - 1] / max) * (height - 4) - 2}
          r="3"
          fill={color}
        />
      )}
    </svg>
  );
};

/** Circular gauge (donut) */
const GaugeChart: React.FC<{
  value: number; // 0–100
  label: string;
  subLabel?: string;
  color: string;
  size?: number;
}> = ({ value, label, subLabel, color, size = 120 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(value, 100) / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-xl font-bold text-gray-900 dark:text-white">{value.toFixed(1)}%</span>
      </div>
      <span className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{label}</span>
      {subLabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400">{subLabel}</span>
      )}
    </div>
  );
};

/** Info tooltip icon with hover popover */
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(prev => !prev)}
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors focus:outline-none"
        aria-label="More info"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 text-xs text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg leading-relaxed">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
            <div className="w-2 h-2 bg-white dark:bg-gray-700 border-r border-b border-gray-200 dark:border-gray-600 rotate-45 -translate-y-1" />
          </div>
        </div>
      )}
    </div>
  );
};

/** Status indicator bar for providers */
const StatusBar: React.FC<{
  label: string;
  available: boolean;
}> = ({ label, available }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center space-x-3">
      <div className={`w-3 h-3 rounded-full ${available ? 'bg-green-500 shadow-green-500/50 shadow-sm' : 'bg-red-500 shadow-red-500/50 shadow-sm'}`}>
        {available && (
          <div className="w-3 h-3 rounded-full bg-green-500 animate-ping opacity-30" />
        )}
      </div>
      <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{label}</span>
    </div>
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
      available
        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
        : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
    }`}>
      {available ? 'Online' : 'Offline'}
    </span>
  </div>
);

const SystemDetails: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prevCpu = useRef<{ user: number; system: number } | null>(null);

  const fetchHealth = async () => {
    try {
      const response = await fetch('/api/admin/system-health', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch system health');
      }

      const data = await response.json();
      const h: SystemHealth = data.health;
      setHealth(h);
      setError(null);

      // Compute CPU delta percentage
      let cpuUserPct = 0;
      let cpuSysPct = 0;
      if (prevCpu.current) {
        const userDelta = h.cpu.user - prevCpu.current.user;
        const sysDelta = h.cpu.system - prevCpu.current.system;
        // Rough: delta microseconds over 5s interval
        cpuUserPct = Math.min((userDelta / 5_000_000) * 100, 100);
        cpuSysPct = Math.min((sysDelta / 5_000_000) * 100, 100);
      }
      prevCpu.current = { user: h.cpu.user, system: h.cpu.system };

      const memPct = (h.memory.heapUsed / h.memory.heapTotal) * 100;

      setHistory(prev => {
        const next = [...prev, {
          timestamp: Date.now(),
          memoryPercent: memPct,
          rss: h.memory.rss,
          cpuUser: cpuUserPct,
          cpuSystem: cpuSysPct,
          mongoMs: h.connections.mongodb.responseTimeMs,
          redisMs: h.connections.redis.responseTimeMs,
        }];
        return next.slice(-MAX_HISTORY);
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatBytes = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading system details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => { setError(null); setLoading(true); fetchHealth(); }}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!health) return null;

  const memPct = (health.memory.heapUsed / health.memory.heapTotal) * 100;
  const memColor = memPct > 80 ? '#ef4444' : memPct > 60 ? '#f59e0b' : '#22c55e';
  const lastCpuEntry = history.length > 0 ? history[history.length - 1] : null;
  const cpuTotal = lastCpuEntry ? Math.min(lastCpuEntry.cpuUser + lastCpuEntry.cpuSystem, 100) : 0;
  const cpuColor = cpuTotal > 80 ? '#ef4444' : cpuTotal > 50 ? '#f59e0b' : '#3b82f6';

  return (
    <div className="space-y-6">
      {/* Top row: Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Memory Gauge */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip text="Heap Memory is the RAM used by the backend for JavaScript objects, variables, and data. High usage (>80%) may indicate a memory leak." />
          </div>
          <GaugeChart
            value={memPct}
            label="Heap Memory"
            subLabel={`${formatBytes(health.memory.heapUsed)} / ${formatBytes(health.memory.heapTotal)}`}
            color={memColor}
          />
        </div>

        {/* CPU Gauge */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip text="CPU Usage shows how much processor time the backend is consuming. 'User' is your app code, 'System' is OS-level operations like I/O." />
          </div>
          <GaugeChart
            value={cpuTotal}
            label="CPU Usage"
            subLabel={`User: ${lastCpuEntry?.cpuUser.toFixed(1) ?? '0'}% / Sys: ${lastCpuEntry?.cpuSystem.toFixed(1) ?? '0'}%`}
            color={cpuColor}
          />
        </div>

        {/* Uptime */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip text="How long the backend server has been running since its last restart. A reset means the server was restarted or redeployed." />
          </div>
          <svg className="w-10 h-10 text-blue-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{formatUptime(health.uptime)}</span>
          <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">Uptime</span>
        </div>

        {/* RSS Memory */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center relative">
          <div className="absolute top-3 right-3">
            <InfoTooltip text="RSS (Resident Set Size) is the total physical memory used by the process, including heap, native code, and buffers. It is always larger than Heap Memory." />
          </div>
          <svg className="w-10 h-10 text-purple-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{formatBytes(health.memory.rss)}</span>
          <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">RSS Memory</span>
        </div>
      </div>

      {/* Sparkline charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Memory over time */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-1.5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Memory Usage Over Time</h3>
              <InfoTooltip text="Tracks heap memory usage percentage over recent polling intervals. A steadily rising trend without drops may indicate a memory leak." />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Last {history.length} samples</span>
          </div>
          <Sparkline data={history.map(h => h.memoryPercent)} color={memColor} width={500} height={80} />
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>{history.length > 0 ? new Date(history[0].timestamp).toLocaleTimeString() : ''}</span>
            <span>Now</span>
          </div>
        </div>

        {/* CPU over time */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-1.5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">CPU Usage Over Time</h3>
              <InfoTooltip text="Shows combined user and system CPU usage over time. Spikes during requests are normal; sustained high usage may need investigation." />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Last {history.length} samples</span>
          </div>
          <Sparkline data={history.map(h => h.cpuUser + h.cpuSystem)} color="#3b82f6" width={500} height={80} />
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>{history.length > 0 ? new Date(history[0].timestamp).toLocaleTimeString() : ''}</span>
            <span>Now</span>
          </div>
        </div>
      </div>

      {/* Database & Provider section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Database Connections */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center space-x-1.5 mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Database Connections</h3>
            <InfoTooltip text="Shows the health and response times of MongoDB (primary data store) and Redis (session/cache store). Green means connected and responding." />
          </div>
          <div className="space-y-4">
            {/* MongoDB */}
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-750 dark:bg-opacity-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${health.connections.mongodb.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-gray-900 dark:text-white">MongoDB</span>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  health.connections.mongodb.status === 'healthy'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                }`}>
                  {health.connections.mongodb.status}
                </span>
              </div>
              {health.connections.mongodb.status === 'healthy' && (
                <div className="grid grid-cols-3 gap-3">
                  {health.connections.mongodb.responseTimeMs !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{health.connections.mongodb.responseTimeMs}<span className="text-xs font-normal">ms</span></div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Latency</div>
                    </div>
                  )}
                  {health.connections.mongodb.poolSize !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{health.connections.mongodb.poolSize}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Pool Size</div>
                    </div>
                  )}
                  {health.connections.mongodb.availableConnections !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{health.connections.mongodb.availableConnections}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Available</div>
                    </div>
                  )}
                </div>
              )}
              {/* Response time sparkline */}
              {history.filter(h => h.mongoMs !== undefined).length >= 2 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Response Time Trend</div>
                  <Sparkline
                    data={history.filter(h => h.mongoMs !== undefined).map(h => h.mongoMs!)}
                    color="#22c55e"
                    width={240}
                    height={40}
                  />
                </div>
              )}
            </div>

            {/* Redis */}
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-750 dark:bg-opacity-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${health.connections.redis.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-gray-900 dark:text-white">Redis</span>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  health.connections.redis.status === 'healthy'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                }`}>
                  {health.connections.redis.status}
                </span>
              </div>
              {health.connections.redis.status === 'healthy' && health.connections.redis.responseTimeMs !== undefined && (
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{health.connections.redis.responseTimeMs}<span className="text-xs font-normal">ms</span></div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Latency</div>
                </div>
              )}
              {history.filter(h => h.redisMs !== undefined).length >= 2 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Response Time Trend</div>
                  <Sparkline
                    data={history.filter(h => h.redisMs !== undefined).map(h => h.redisMs!)}
                    color="#ef4444"
                    width={240}
                    height={40}
                  />
                </div>
              )}
            </div>

            {/* WebSocket */}
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-750 dark:bg-opacity-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="font-medium text-gray-900 dark:text-white">WebSocket</span>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {health.connections.websocket.activeConnections} <span className="text-xs font-normal text-gray-500">connections</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* LLM Providers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center space-x-1.5 mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">LLM Provider Status</h3>
            <InfoTooltip text="Real-time availability of each LLM API provider. 'Online' means the API key is valid and the provider is responding to requests." />
          </div>
          {Object.keys(health.providers).length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No providers configured</p>
          ) : (
            <div className="space-y-1 divide-y divide-gray-100 dark:divide-gray-700">
              {Object.entries(health.providers).map(([provider, available]) => (
                <StatusBar key={provider} label={provider} available={available} />
              ))}
            </div>
          )}

          {/* Provider summary */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-around">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Object.values(health.providers).filter(Boolean).length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Online</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {Object.values(health.providers).filter(v => !v).length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Offline</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.keys(health.providers).length}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-xs text-gray-400 dark:text-gray-500 text-center">
        Auto-refreshing every 5 seconds &middot; Last updated: {new Date(health.timestamp).toLocaleTimeString()}
      </div>
    </div>
  );
};

export default SystemDetails;
